/**
 * RoleGate · route-level gate by logistics role.
 *
 * Sibling to PhaseGate (which gates by feature flag). Use this for surfaces that
 * are scoped to specific roles in the sidebar but whose routes would otherwise
 * be reachable by URL. Example: My Trust Score is shown to carriers + brokers,
 * so a dispatcher hitting /dash/carriers/my-score directly should be redirected.
 *
 *   <RoleGate allow={['carrier', 'broker']}><MyTrustScorePage /></RoleGate>
 *
 * Platform admins bypass by default (allowAdmin=true) so Jack/Louis can preview.
 * Unknown/loading role falls open — same "never lock out a confused user"
 * philosophy as the sidebar's buildVisibleNav. On deny: redirect + toast.
 */
import { ReactNode, useEffect, useRef } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useRequireRole } from "@/hooks/useRequireRole";
import { toast } from "sonner";

export type GateRole = "carrier" | "broker" | "dispatcher";

interface RoleGateProps {
  /** Roles allowed to view this route. Others are redirected (admins bypass). */
  allow: GateRole[];
  /** Where to send blocked users. Defaults to "/dash". */
  redirectTo?: string;
  /** If true, platform admins bypass the gate. Defaults to true. */
  allowAdmin?: boolean;
  /** Toast message on deny. */
  denyMessage?: string;
  children: ReactNode;
}

export function RoleGate({
  allow,
  redirectTo = "/dash",
  allowAdmin = true,
  denyMessage,
  children,
}: RoleGateProps) {
  const { companyId } = useAuth();
  const { hasRole: isAdmin, loading: adminLoading } = useRequireRole("admin");
  const location = useLocation();
  const toastedRef = useRef(false);

  const { data: role, isLoading } = useQuery({
    queryKey: ["role-gate", "role", companyId],
    enabled: !!companyId,
    staleTime: 60_000,
    queryFn: async (): Promise<GateRole | null> => {
      const { data } = await supabase
        .from("companies")
        .select("logistics_role")
        .eq("id", companyId!)
        .maybeSingle();
      return (data?.logistics_role ?? null) as GateRole | null;
    },
  });

  // While resolving role/admin, render nothing to avoid a flash or a wrong
  // redirect before we know who the user is.
  if ((!!companyId && isLoading) || adminLoading) return null;

  if (allowAdmin && isAdmin) return <>{children}</>;
  // Fall open when the role is unknown so we never lock out a confused user.
  if (!role || allow.includes(role)) return <>{children}</>;

  return (
    <RoleGateDeny
      redirectTo={redirectTo}
      denyMessage={denyMessage}
      from={location.pathname}
      toastedRef={toastedRef}
    />
  );
}

function RoleGateDeny({
  redirectTo,
  denyMessage,
  from,
  toastedRef,
}: {
  redirectTo: string;
  denyMessage?: string;
  from: string;
  toastedRef: React.MutableRefObject<boolean>;
}) {
  useEffect(() => {
    if (toastedRef.current) return;
    toastedRef.current = true;
    toast.message(denyMessage ?? "That page isn't available for your account type.");
  }, [denyMessage, toastedRef]);

  return <Navigate to={redirectTo} replace state={{ from }} />;
}

export default RoleGate;
